import { BookingStatus } from '../models/BookingStatus';
import { PaymentActivityType } from '../models/PaymentActivityType';
import { RecurrenceType } from '../models/RecurrenceType';
import { mapRecurrenceTypeToFrequency } from './RecurrenceTypeMapper';

/**
 * Maps BookingStatus enum to display string
 * @param status - The BookingStatus enum value
 * @returns Display string for the status
 */
export const mapBookingStatusToDisplay = (status: BookingStatus): string => {
  switch (status) {
    case BookingStatus.Active:
      return 'Active';
    case BookingStatus.Cancelled:
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

/**
 * Maps PaymentActivityType enum to display string
 * @param type - The PaymentActivityType enum value
 * @returns Display string for the payment type
 */
export const mapPaymentActivityTypeToDisplay = (type: PaymentActivityType): string => {
  switch (type) {
    case PaymentActivityType.Free:
      return 'Free';
    case PaymentActivityType.Regular:
      return 'Regular';
    case PaymentActivityType.Extended:
      return 'Extended';
    default:
      return 'Unknown';
  }
};

/**
 * Maps RecurrenceType enum to display string
 * @param type - The RecurrenceType enum value
 * @returns Display string for the recurrence type
 */
export const mapRecurrenceTypeToDisplay = (type: RecurrenceType): string => {
  // Use the existing mapper to get the frequency string, then convert to display text
  const frequency = mapRecurrenceTypeToFrequency(type);
  
  switch (frequency) {
    case 'none':
      return 'Does not repeat';
    case 'daily':
      return 'Daily';
    case 'weekday':
      return 'Daily (Workdays)';
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      return 'Monthly';
    default:
      // Handle other recurrence types that might not be mapped to frequency strings
      switch (type) {
        case RecurrenceType.DailyWeekend:
          return 'Daily (Weekend)';
        case RecurrenceType.BiWeekly:
          return 'Bi-Weekly';
        case RecurrenceType.EveryThirdWeek:
          return 'Every Third Week';
        case RecurrenceType.EveryFourthWeek:
          return 'Every Fourth Week';
        case RecurrenceType.MonthlyFirstDay:
          return 'Monthly (First Day)';
        default:
          return 'Unknown';
      }
  }
};

/**
 * Formats time from HH:mm format to 12-hour format with am/pm
 * @param timeLocal - Time in HH:mm format
 * @returns Formatted time string
 */
export const formatTimeDisplay = (timeLocal: string): string => {
  const [hours, minutes] = timeLocal.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
};

/**
 * Calculates end time based on start time and duration
 * @param timeLocal - Start time in HH:mm format
 * @param duration - Duration in minutes
 * @returns End time in 12-hour format
 */
export const calculateEndTimeDisplay = (timeLocal: string, duration: number): string => {
  const [hours, minutes] = timeLocal.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + duration;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  
  const period = endHours >= 12 ? 'PM' : 'AM';
  const displayHours = endHours === 0 ? 12 : endHours > 12 ? endHours - 12 : endHours;
  return `${displayHours}:${endMinutes.toString().padStart(2, '0')}${period}`;
};

/**
 * Formats date to long format
 * @param date - Date object
 * @returns Formatted date string
 */
export const formatDateDisplay = (date: Date | string): string => {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
};

/**
 * Formats duration in minutes to hours and minutes display
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 */
export const formatDurationDisplay = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins} minutes`;
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minutes`;
};
